/**
 * EventThemeCard component for displaying an event in theme blocks
 * Uses shared components from event-card-shared.tsx
 */

'use client';

import type { EventTeaser } from '../types';
import {
  EventCardCompact,
  EventCardDefaultNonClickable,
  useEventCardData
} from './event-card-shared';

export type EventThemeCardLayout = 'grid' | 'list' | 'compact';

interface EventThemeCardProps {
  event: EventTeaser;
  /** Base URL for event links (e.g., '/events') */
  baseUrl?: string;
  /** Layout variant for the card - maps to EventCard variant */
  layout?: EventThemeCardLayout;
  /** Show past events differently */
  showPastIndicator?: boolean;
  // Legacy props for compatibility (not used but kept for interface compatibility)
  showImage?: boolean;
  showDate?: boolean;
  showLocation?: boolean;
  showEventType?: boolean;
  showExcerpt?: boolean;
}

/**
 * EventThemeCard component displays an event in a card format
 * Uses the same shared rendering components as EventCard
 */
export function EventThemeCard({
  event,
  baseUrl = '/events',
  layout = 'grid',
  showPastIndicator = true,
  // Legacy props (not used)
  showImage,
  showDate,
  showLocation,
  showEventType,
  showExcerpt,
}: EventThemeCardProps) {
  // Construct event URL - event.path already contains full path like /events/title
  const eventUrl = event.path;

  if (!eventUrl) return null;

  // Extract event data using shared hook
  const data = useEventCardData(event);

  // Map layout to variant: 'compact' -> 'compact', 'grid'/'list' -> 'default'
  const variant = layout === 'compact' ? 'compact' : 'default';

  if (variant === 'compact') {
    return <EventCardCompact event={event} eventUrl={eventUrl} data={data} />;
  }

  // Use non-clickable version with action buttons for theme cards
  return (
    <EventCardDefaultNonClickable
      event={event}
      eventUrl={eventUrl}
      data={data}
      showPastIndicator={showPastIndicator}
    />
  );
}
